import { z } from "zod";

/**
 * npm package names, comma-separated. The endpoint is public, so the shape is
 * kept tight: a bounded list of valid npm names, nothing free-form.
 */
export const npmDownloadsQuerySchema = z.object({
  packages: z.string().min(1).max(400),
});

export type NpmDownloadsQuery = z.infer<typeof npmDownloadsQuerySchema>;

/** One month of combined downloads across the requested packages. */
export type NpmMonthlyDownloads = {
  /** ISO month, e.g. "2026-08". */
  month: string;
  downloads: number;
};

export type NpmDownloadsResult = {
  packages: string[];
  /** Sum of every month in `monthly`. */
  totalLastYear: number;
  /** Downloads in the most recent full month. */
  lastMonth: number;
  monthly: NpmMonthlyDownloads[];
};

/** One month of revenue, in the account's major currency units. */
export type RevenueMonthlyPoint = {
  /** ISO month, e.g. "2026-08". */
  month: string;
  value: number;
};

export type RevenueResult = {
  /** False when no Polar access token is configured; the frontend then uses its manual numbers. */
  available: boolean;
  /** Lowercase ISO currency the revenue is reported in, e.g. "usd". Null when unavailable. */
  currency: string | null;
  /** Revenue in the last complete month. */
  lastMonth: number;
  monthly: RevenueMonthlyPoint[];
};

/** Glyphs the badge tile can show; the server owns the SVG paths. */
export const projectBadgeIcons = ["zap", "hype-stack", "pinch", "lens", "star"] as const;

export type ProjectBadgeIcon = (typeof projectBadgeIcons)[number];

/**
 * A live SVG badge for one project, meant to be embedded in READMEs. Public
 * and stateless, so every input is bounded: a short label, npm names, one
 * `owner/name` repo, a glyph from a fixed set, and a bare hex accent.
 */
export const projectBadgeQuerySchema = z.object({
  label: z.string().trim().min(1).max(40),
  /** npm package names, comma-separated; their last full month is summed. */
  packages: z.string().min(1).max(400).optional(),
  /** GitHub repository as `owner/name`, for the star count. */
  repo: z
    .string()
    .regex(/^[\w.-]+\/[\w.-]+$/, "Expected a GitHub repository as owner/name.")
    .optional(),
  icon: z.enum(projectBadgeIcons).default("zap"),
  /** Accent color as six hex digits without the hash. */
  color: z
    .string()
    .regex(/^[0-9a-f]{6}$/i, "Expected six hex digits.")
    .default("f97316"),
});

export type ProjectBadgeQuery = z.infer<typeof projectBadgeQuerySchema>;
